/**
 * @fileoverview PeopleGrid Component - Dynamic grid display for member profiles
 * 
 * Provides a flexible grid layout for displaying member profiles with:
 * - Filterable member display with color-coded categories
 * - Optimized image loading with caching and batch processing
 * - Lazy loading for improved performance
 * - Profile image loading with fallback initials
 * - Modal integration for detailed profile views
 * - Responsive grid layout with configurable columns
 * - Loading states and error handling
 * - Sorting by position and role
 * - Tooltip integration for filters
 * - Memory management for blob URLs
 * 
 * @component
 * @example
 * <PeopleGrid 
 *   data={members} 
 *   filterField="representing" 
 *   colorMap={colorMapping}
 *   gridColumns={4}
 *   showRole={true}
 *   modalType="council"
 * />
 * 
 * @requires React
 * @requires @tippyjs/react
 * @requires ./PeopleGrid.css
 * @requires ../../modals/ProfileModal
 * @requires ../../utils/apiHelper
 * @requires ./LoadingSpinner
 * 
 * @author By John Bellew - Infinia.ie - last updated 15/07/2025
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import './PeopleGrid.css';
import ProfileModal from '../../modals/ProfileModal';
import apiHelper from '../../utils/apiHelper';
import LoadingSpinner from './LoadingSpinner';

// Cache configuration
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
const CONCURRENT_LOADS = 3; // Limit concurrent image loads
const MAX_CACHE_SIZE = 4 * 1024 * 1024; // 4MB max cache size
const getCacheKey = (email) => `member_image_${email}`;

// Cache utilities with quota management
const getFromCache = (email) => {
    try {
        const cached = localStorage.getItem(getCacheKey(email));
        if (cached) {
            const { url, timestamp, accessCount = 0 } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION) {
                // Update access count for LRU tracking
                updateCacheAccess(email, accessCount + 1);
                return url;
            }
            localStorage.removeItem(getCacheKey(email));
        }
    } catch (error) {
        console.error('Cache read error:', error);
    }
    return null;
};

const updateCacheAccess = (email, accessCount) => {
    try {
        const cached = localStorage.getItem(getCacheKey(email));
        if (cached) {
            const data = JSON.parse(cached);
            data.accessCount = accessCount;
            data.lastAccess = Date.now();
            localStorage.setItem(getCacheKey(email), JSON.stringify(data));
        }
    } catch (error) {
        // Silent fail for access count updates
    }
};

const getCurrentCacheSize = () => {
    const keys = Object.keys(localStorage).filter(key => key.startsWith('member_image_'));
    return keys.reduce((size, key) => {
        const item = localStorage.getItem(key);
        return size + (item ? item.length : 0);
    }, 0);
};

const evictLeastRecentlyUsed = () => {
    const keys = Object.keys(localStorage).filter(key => key.startsWith('member_image_'));
    const cacheEntries = keys.map(key => {
        try {
            const data = JSON.parse(localStorage.getItem(key));
            return {
                key,
                email: key.replace('member_image_', ''),
                lastAccess: data.lastAccess || data.timestamp,
                accessCount: data.accessCount || 0,
                size: localStorage.getItem(key).length
            };
        } catch (error) {
            return { key, lastAccess: 0, accessCount: 0, size: 0 };
        }
    }).sort((a, b) => {
        // Sort by access count (ascending) then by last access (ascending)
        if (a.accessCount !== b.accessCount) {
            return a.accessCount - b.accessCount;
        }
        return a.lastAccess - b.lastAccess;
    });

    // Remove the least recently used items until we free up some space
    let freedSpace = 0;
    let removed = 0;
    for (const entry of cacheEntries) {
        localStorage.removeItem(entry.key);
        freedSpace += entry.size;
        removed++;
        
        // Free up at least 25% of max cache size
        if (freedSpace >= MAX_CACHE_SIZE * 0.25) {
            break;
        }
    }
    
    // Evicted ${removed} cached images to free ${Math.round(freedSpace / 1024)}KB
    return removed;
};

const setToCache = (email, url) => {
    try {
        const cacheData = {
            url,
            timestamp: Date.now(),
            lastAccess: Date.now(),
            accessCount: 1
        };
        
        const dataString = JSON.stringify(cacheData);
        const currentSize = getCurrentCacheSize();
        
        // Check if adding this item would exceed the quota
        if (currentSize + dataString.length > MAX_CACHE_SIZE) {
            // Cache approaching limit, evicting old items
            evictLeastRecentlyUsed();
        }
        
        localStorage.setItem(getCacheKey(email), dataString);
    } catch (error) {
        if (error.name === 'QuotaExceededError') {
          
            try {
                evictLeastRecentlyUsed();
                // Try again after eviction
                localStorage.setItem(getCacheKey(email), JSON.stringify({
                    url,
                    timestamp: Date.now(),
                    lastAccess: Date.now(),
                    accessCount: 1
                }));
            } catch (secondError) {
                console.log(`⚠️ Unable to cache image for ${email}, storage quota full`);
            }
        } else {
            console.error('Cache write error:', error);
        }
    }
};

// Cache migration - clear old blob URLs that are no longer valid
const migrateCacheIfNeeded = () => {
    const keys = Object.keys(localStorage).filter(key => key.startsWith('member_image_'));
    let migratedCount = 0;
    
    keys.forEach(key => {
        try {
            const cached = localStorage.getItem(key);
            if (cached) {
                const { url } = JSON.parse(cached);
                // Remove old blob URLs that are no longer valid
                if (url?.startsWith('blob:')) {
                    localStorage.removeItem(key);
                    migratedCount++;
                }
            }
        } catch (error) {
            // Remove corrupted cache entries
            localStorage.removeItem(key);
            migratedCount++;
        }
    });
    
    // Migrated cache: removed invalid blob URLs if needed
};

// Global cache management functions for debugging
const getCacheInfo = () => {
    const keys = Object.keys(localStorage).filter(key => key.startsWith('member_image_'));
    const currentSize = getCurrentCacheSize();
    const info = {
        totalCachedImages: keys.length,
        cacheSize: currentSize,
        cacheSizeKB: Math.round(currentSize / 1024),
        cacheSizeMB: Math.round(currentSize / (1024 * 1024) * 100) / 100,
        maxCacheSizeMB: Math.round(MAX_CACHE_SIZE / (1024 * 1024) * 100) / 100,
        cacheUtilization: Math.round((currentSize / MAX_CACHE_SIZE) * 100),
        cachedEmails: keys.map(key => {
            const email = key.replace('member_image_', '');
            const cached = localStorage.getItem(key);
            if (cached) {
                try {
                    const { timestamp, url, accessCount = 0, lastAccess } = JSON.parse(cached);
                    return {
                        email,
                        cachedAt: new Date(timestamp).toLocaleString(),
                        lastAccess: lastAccess ? new Date(lastAccess).toLocaleString() : 'Never',
                        ageMinutes: Math.floor((Date.now() - timestamp) / 60000),
                        accessCount,
                        sizeKB: Math.round(cached.length / 1024),
                        type: url?.startsWith('data:') ? 'base64' : 'unknown'
                    };
                } catch (error) {
                    return { email, error: 'corrupted' };
                }
            }
            return null;
        }).filter(Boolean).sort((a, b) => b.accessCount - a.accessCount) // Sort by most accessed
    };
   
    return info;
};

const clearMemberImageCache = () => {
    const keys = Object.keys(localStorage).filter(key => key.startsWith('member_image_'));
    keys.forEach(key => localStorage.removeItem(key));
   
    return keys.length;
};

const optimizeCache = () => {
    const info = getCacheInfo();
    if (info.cacheUtilization > 80) {
       
        const evicted = evictLeastRecentlyUsed();
        const newInfo = getCacheInfo();
       
        return { before: info, after: newInfo, evicted };
    } else {
       
        return { message: 'No optimization needed', utilization: info.cacheUtilization };
    }
};

// Expose functions globally for debugging
if (typeof window !== 'undefined') {
    window.getMemberImageCacheInfo = getCacheInfo;
    window.clearMemberImageCache = clearMemberImageCache;
    window.optimizeMemberImageCache = optimizeCache;
}

/**
 * PeopleGrid Component
 * 
 * Renders a filterable grid of member profiles with optimized image loading.
 * Features progressive loading, caching, and lazy loading for better performance.
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {Array} props.data - Array of member data objects
 * @param {string} [props.filterField='representing'] - Field to filter members by
 * @param {Object} [props.colorMap={}] - Color mapping for filter categories
 * @param {number} [props.gridColumns=5] - Number of columns in grid layout
 * @param {boolean} [props.showRole=true] - Whether to display member roles
 * @param {boolean} [props.showFilters=true] - Whether to show filter controls
 * @param {string} [props.modalType='council'] - Type of modal for profile display
 * 
 * @returns {JSX.Element} Filterable member grid with optimized image loading
 */
const PeopleGrid = ({
    data,
    filterField = 'representing',
    colorMap = {},
    gridColumns = 5,
    showRole = true,
    showFilters = true,
    modalType = 'council'
}) => {
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [filters, setFilters] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [memberImages, setMemberImages] = useState({});
    const [loadingImages, setLoadingImages] = useState({});
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const memberImagesRef = useRef({});
    const loadQueue = useRef([]);
    const activeLoads = useRef(0);

    const handleProfileClick = (profileData) => {
        // For council members, pass the full data from the JSON including jobTitle and memberCohort
        const fullProfileData = {
            ...profileData, // This includes jobTitle, memberCohort, and all other council data
            type: modalType // This will be 'council' for council members
        };
        
        setProfileData(fullProfileData);
        setShowProfileModal(true);
    }

    useEffect(() => {
        if (showFilters) {
            const uniqueFilters = [...new Set(data.map(item => item[filterField]))];
            setFilters(uniqueFilters);
        } else {
            setFilters([]);
            setSelectedFilters([]);
        }
    }, [data, filterField, showFilters]);

    const handleFilterChange = (value) => {
        setSelectedFilters(prev =>
            prev.includes(value)
                ? prev.filter(item => item !== value)
                : [...prev, value]
        );
    };

    const getFilterColor = (value) => {
        return colorMap[value] || 'var(--ifia-grey)';
    };

    const filteredData = data.filter(item => {
        return selectedFilters.length === 0 || selectedFilters.includes(item[filterField]);
    });

    const getInitials = (name) => {
        if (!name) return '';
        return name.split(' ').map(n => n[0]).join('');
    };

    const sortedData = [...filteredData].sort((a, b) => {
        // First check if positionOnGrid exists for both items
        if (a.positionOnGrid !== undefined && b.positionOnGrid !== undefined) {
            return a.positionOnGrid - b.positionOnGrid;
        }
        // If only one has positionOnGrid, prioritize the one that has it
        if (a.positionOnGrid !== undefined) return -1;
        if (b.positionOnGrid !== undefined) return 1;
        
        // Fall back to role-based sorting if no positionOnGrid
        const roles = ["Past Chair", "Vice Chair", "Chair"];
        return roles.indexOf(b.role || '') - roles.indexOf(a.role || '');
    });

    const hasActiveFilters = () => selectedFilters.length > 0;

    // Compress image to reduce cache size
    const compressImage = useCallback(async (blob) => {
        return new Promise((resolve) => {
            const img = new Image();
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            img.onload = () => {
                // Calculate new dimensions (max 200x200 for profile images)
                const maxSize = 200;
                let { width, height } = img;
                
                if (width > height) {
                    if (width > maxSize) {
                        height = (height * maxSize) / width;
                        width = maxSize;
                    }
                } else {
                    if (height > maxSize) {
                        width = (width * maxSize) / height;
                        height = maxSize;
                    }
                }
                
                canvas.width = width;
                canvas.height = height;
                
                // Draw and compress
                ctx.drawImage(img, 0, 0, width, height);
                
                // Convert to base64 with compression (0.7 = 70% quality)
                const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
                resolve(compressedBase64);
            };
            
            img.onerror = () => {
                // Fallback to original if compression fails
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            };
            
            img.src = URL.createObjectURL(blob);
        });
    }, []);

    // Load single member image with caching
    const loadSingleMemberImage = useCallback(async (member) => {
        if (!member.email) return null;

        // Check cache first
        const cachedUrl = getFromCache(member.email);
        if (cachedUrl) {
            return { email: member.email, url: cachedUrl };
        }

        try {
            // First get the profile to get the photo
            const profile = await apiHelper.getMemberProfileByEmail(member.email);
            if (!profile?.photo) {
                return null;
            }

            // Get the image using the photo filename
            const authToken = await apiHelper.getAuthToken();
            const response = await fetch(`${process.env.REACT_APP_API_URL}graph/getMediaContent/profile-images/${profile.photo}`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });

            if (!response.ok) {
                return null;
            }

            const blob = await response.blob();
            
            // Compress image to reduce cache size
            const compressedBase64 = await compressImage(blob);
            
            // Cache the compressed base64 data (persistent across sessions)
            setToCache(member.email, compressedBase64);
            
            return { email: member.email, url: compressedBase64 };
        } catch (error) {
            console.error(`Error loading image for member ${member.email}:`, error);
            return null;
        }
    }, [compressImage]);

    // Process load queue with concurrency limit
    const processLoadQueue = useCallback(async () => {
        while (loadQueue.current.length > 0 && activeLoads.current < CONCURRENT_LOADS) {
            const member = loadQueue.current.shift();
            if (!member) continue;

            activeLoads.current++;
            setLoadingImages(prev => ({ ...prev, [member.email]: true }));

            try {
                const result = await loadSingleMemberImage(member);
                if (result) {
                    setMemberImages(prev => {
                        const newImages = { ...prev, [result.email]: result.url };
                        memberImagesRef.current = newImages;
                        return newImages;
                    });
                }
            } catch (error) {
                console.error(`Error loading image for ${member.email}:`, error);
            } finally {
                activeLoads.current--;
                setLoadingImages(prev => {
                    const newLoading = { ...prev };
                    delete newLoading[member.email];
                    return newLoading;
                });
                
                // Process next items in queue
                if (loadQueue.current.length > 0) {
                    processLoadQueue();
                }
            }
        }
    }, [loadSingleMemberImage]);

    // Initialize image loading with cache check
    const initializeImageLoading = useCallback(async () => {
        // Clean up old blob URLs from previous versions
        migrateCacheIfNeeded();
        
        const membersWithEmail = data.filter(member => member.email);
        
        // Check cache for all members first
        const cachedImages = {};
        const membersToLoad = [];
        
        membersWithEmail.forEach(member => {
            const cachedUrl = getFromCache(member.email);
            if (cachedUrl) {
                cachedImages[member.email] = cachedUrl;
            } else {
                membersToLoad.push(member);
            }
        });

        // Set cached images immediately
        if (Object.keys(cachedImages).length > 0) {
            setMemberImages(cachedImages);
            memberImagesRef.current = cachedImages;
        }

        // Queue remaining members for loading
        loadQueue.current = [...membersToLoad];
        
        // Start processing queue
        processLoadQueue();
        
        // Initial loading is complete (cached images are shown)
        setIsInitialLoading(false);
    }, [data, processLoadQueue]);

    useEffect(() => {
        initializeImageLoading();
    }, [initializeImageLoading]);

    // Cleanup effect (no longer needed for base64 data URLs, but kept for potential future use)
    useEffect(() => {
        return () => {
            // Base64 data URLs don't need cleanup like blob URLs did
            // This effect is kept for potential future cleanup needs
        };
    }, []); // Empty dependency array - only run on mount/unmount

    return (
        <div className="people-grid">
            <div className="people-grid-content">
                {showFilters && (
                    <div className="people-filters pb-4">
                        <div className="filter-section">
                            {filters.map(item => (
                                <div key={item} className="filter-item">
                                    <Tippy content={item}>
                                        <div className="filter-button-container" style={{ borderRight: `25px solid ${getFilterColor(item)}` }}>
                                            <button
                                                className={`filter-button ${selectedFilters.includes(item) ? 'selected' : ''}`}
                                                onClick={() => handleFilterChange(item)}
                                            >
                                                <span className="filter-button-text">{item}</span>
                                            </button>
                                        </div>
                                    </Tippy>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {isInitialLoading && Object.keys(memberImages).length === 0 ? (
                    <LoadingSpinner />
                ) : (
                    <div
                        className={`people-member-grid ${hasActiveFilters() ? 'is-filtered' : ''}`}
                        style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }}
                    >
                        {sortedData.map((member, index) => (
                            <div key={index} className="member-card pointer" onClick={() => handleProfileClick(member)}>
                                <div className="member-image-container">
                                    {member.image ? (
                                        <img
                                            src={member.image}
                                            alt={member.name}
                                            className="member-image"
                                        />
                                    ) : member.email && memberImages[member.email] ? (
                                        <img
                                            src={memberImages[member.email]}
                                            alt={member.name}
                                            className="member-image"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.style.display = 'none';
                                                e.target.parentElement.classList.add('member-initials');
                                                e.target.parentElement.textContent = getInitials(member.name);
                                            }}
                                        />
                                    ) : loadingImages[member.email] ? (
                                        <div className="member-image-skeleton">
                                            <div className="skeleton-pulse"></div>
                                        </div>
                                    ) : (
                                        <div className="member-initials">
                                            {getInitials(member.name)}
                                        </div>
                                    )}
                                </div>
                                {showFilters && (
                                    <div
                                        className="representing-bar"
                                        style={{ backgroundColor: getFilterColor(member[filterField]) }}
                                    />
                                )}
                                <div className="member-info">
                                    <h5>{member.name}</h5>
                                    <div className="hover-content">
                                        <p className="member-company">
                                            {member.company}
                                        </p>
                                        {showRole && member.role && member.role !== 'Member' && (
                                            <p className="member-role">
                                                {member.role}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showProfileModal && (
                <ProfileModal
                    show={showProfileModal}
                    onClose={() => setShowProfileModal(false)}
                    profileData={profileData}
                    type={modalType}
                />
            )}
        </div>
    );
};

export default PeopleGrid; 